// ============================================================
// RB-PMS v3 — All Module Services
// File: src/services/index.ts
// ============================================================

import api from '../utils/api';

// ── Material Service ──────────────────────────────────────────
export const materialService = {
  getStats:     ()             => api.get('/material/stats'),
  getAll:       (params?: any) => api.get('/material/materials', { params }),
  getById:      (id: number)   => api.get(`/material/materials/${id}`),
  getVariants:  (matId: number)=> api.get(`/material/materials/${matId}/variants`),
  getSuppliers: (params?: any) => api.get('/material/suppliers', { params }),
  getL1Rates:   (matId: number)=> api.get(`/material/materials/${matId}/l1-rates`),
};

// ── Machinery Service ─────────────────────────────────────────
export const machineryService = {
  getStats:     ()             => api.get('/machinery/stats'),
  getAll:       (params?: any) => api.get('/machinery', { params }),
  getById:      (id: number)   => api.get(`/machinery/${id}`),
  updateStatus: (id: number, status: string, project_id?: number) =>
    api.patch(`/machinery/${id}/status`, { status, project_id }),
  // Vendors
  getVendors:   (params?: any) => api.get('/machinery/vendors/all', { params }),
  getVendorById:(id: number)   => api.get(`/machinery/vendors/${id}`),
  // Vendor Rates
  getVendorRates:   (vndId: number) => api.get(`/machinery/vendors/${vndId}/rates`),
  getRatesByMchType:(mchType: string) => api.get(`/machinery/vendor-rates/${mchType}`),
  // Activity Mappings
  getActivityMappings: () => api.get('/machinery/activity-mappings'),
};

// ── Tools Service ─────────────────────────────────────────────
export const toolsService = {
  getStats:   ()             => api.get('/tools/stats'),
  getAll:     (params?: any) => api.get('/tools/', { params }),
  getById:    (id: number)   => api.get(`/tools/${id}`),
  issue:      (id: number, qty: number, project_id?: number) =>
    api.patch(`/tools/${id}/issue`, { qty, project_id }),
  return_:    (id: number, qty: number, issue_id?: number, condition_on_return?: string) =>
    api.patch(`/tools/${id}/return`, { qty, issue_id, condition_on_return }),
  damage:     (id: number, qty: number) => api.patch(`/tools/${id}/damaged`, { qty }),
  // Stock & Repair
  getStock:   (id: number)   => api.get(`/tools/${id}/stock`),
  getRepair:  (id: number)   => api.get(`/tools/${id}/repair`),
  // Suppliers
  getSuppliers:     (params?: any) => api.get('/tools/suppliers', { params }),
  getSupplierRates: (tspId: number)=> api.get(`/tools/suppliers/${tspId}/rates`),
  // Activity Mappings
  getActivityMappings: () => api.get('/tools/activity-mappings'),
  // Issue Register
  getIssueRegister: (params?: any) => api.get('/tools/issue-register', { params }),
};

// ── HR Service ────────────────────────────────────────────────
export const hrService = {
  getStats:      ()             => api.get('/hr/stats'),
  getEmployees:  (params?: any) => api.get('/hr/employees', { params }),
  getEmployee:   (id: number)   => api.get(`/hr/employees/${id}`),
  getSkills:     (params?: any) => api.get('/hr/skills', { params }),
  getManpowerMappings: ()       => api.get('/hr/manpower-mappings'),
};

// ── Activity Service ──────────────────────────────────────────
export const activityService = {
  getStats:           ()                    => api.get('/activity/stats'),
  getStages:          ()                    => api.get('/activity/stages'),
  getTree:            ()                    => api.get('/activity/tree'),
  getMilestones:      ()                    => api.get('/activity/milestones'),
  search:             (q: string)           => api.get('/activity/search', { params: { q } }),
  getProcesses:       (stageCode: string)   => api.get(`/activity/stages/${stageCode}/processes`),
  getSubProcesses:    (processCode: string) => api.get(`/activity/processes/${processCode}/sub-processes`),
  getActivities:      (subCode: string)     => api.get(`/activity/sub-processes/${subCode}/activities`),
  getActivity:        (id: number)          => api.get(`/activity/activities/${id}`),
  // Create
  createStage:        (data: any)           => api.post('/activity/stages', data),
  createProcess:      (data: any)           => api.post('/activity/processes/by-id', data),
  createSubProcess:   (data: any)           => api.post('/activity/sub-processes/by-id', data),
  createActivity:     (data: any)           => api.post('/activity/activities', data),
};

// ── Estimation Service ────────────────────────────────────────
export const estimationService = {
  getStats:    ()             => api.get('/estimation/stats'),
  getTiers:    (params: any)  => api.get('/estimation/tiers', { params }),
  calculate:   (data: any)    => api.post('/estimation/calculate', data),
  save:        (data: any)    => api.post('/estimation/save', data),
  getAll:      (params?: any) => api.get('/estimation', { params }),
  getById:     (id: number)   => api.get(`/estimation/${id}`),
  updateStatus:(id: number, status: string) => api.patch(`/estimation/${id}/status`, { status }),
  updateNotes: (id: number, data: any) => api.patch(`/estimation/${id}/notes`, data),
  getByLead:   (leadId: number) => api.get(`/estimation/lead/${leadId}`),
};

// ── Subcontractor Service ─────────────────────────────────────
export const subcontractorService = {
  getAll:   (params?: any) => api.get('/hr/subcontractors', { params }),
  getById:  (id: number)   => api.get(`/hr/subcontractors/${id}`),
  create:   (data: any)    => api.post('/hr/subcontractors', data),
  update:   (id: number, data: any) => api.put(`/hr/subcontractors/${id}`, data),
};

// ── Lead & Customer Service ───────────────────────────────────
export const leadService = {
  getStats:     ()             => api.get('/leads/stats'),
  getAll:       (params?: any) => api.get('/leads', { params }),
  getById:      (id: number)   => api.get(`/leads/${id}`),
  getFollowups: (leadId: number) => api.get(`/leads/${leadId}/followups`),
  getDocuments: (leadId: number) => api.get(`/leads/${leadId}/documents`),
};

export const customerService = {
  getAll:   (params?: any) => api.get('/customers', { params }),
  getById:  (id: number)   => api.get(`/customers/${id}`),
  getDocuments: (customerId: number) => api.get(`/customers/${customerId}/documents`),
};

// ── Project Service ───────────────────────────────────────────
export const projectService = {
  getStats:    ()             => api.get('/projects/stats'),
  getAll:      (params?: any) => api.get('/projects/', { params }),
  getById:     (id: number)   => api.get(`/projects/${id}`),
  getDashboard:(id: number)   => api.get(`/projects/${id}/dashboard`),
  create:      (data: any)    => api.post('/projects/', data),
  update:      (id: number, data: any) => api.put(`/projects/${id}`, data),
  // Blocks
  getBlocks:   (projectId: number) => api.get(`/projects/${projectId}/blocks`),
  createBlock: (projectId: number, data: any) => api.post(`/projects/${projectId}/blocks`, data),
  // Floors
  getFloors:   (projectId: number) => api.get(`/projects/${projectId}/floors`),
  createFloor: (projectId: number, data: any) => api.post(`/projects/${projectId}/floors`, data),
  bulkCreateFloors: (projectId: number, data: any) => api.post(`/projects/${projectId}/floors/bulk-create`, data),
  // Template
  getTemplate:      (projectId: number, params?: any) => api.get(`/projects/${projectId}/template`, { params }),
  generateTemplate: (projectId: number) => api.post(`/projects/${projectId}/template/generate`),
  approveTemplate:  (projectId: number) => api.post(`/projects/${projectId}/template/approve`),
  lockTemplate:     (projectId: number) => api.post(`/projects/${projectId}/template/lock`),
  // Schedule
  getSchedule:  (projectId: number, params?: any) => api.get(`/projects/${projectId}/schedule`, { params }),
  upsertSchedule: (projectId: number, data: any)  => api.post(`/projects/${projectId}/schedule`, data),
  // Progress
  getProgress:  (projectId: number, params?: any) => api.get(`/projects/${projectId}/progress`, { params }),
  markProgress: (projectId: number, data: any)    => api.post(`/projects/${projectId}/progress`, data),
  // Budget
  getBudget:    (projectId: number) => api.get(`/projects/${projectId}/budget`),
  upsertBudget: (projectId: number, data: any) => api.post(`/projects/${projectId}/budget`, data),
  // Documents
  getDocuments: (projectId: number) => api.get(`/projects/${projectId}/documents`),
  addDocument:  (projectId: number, data: any) => api.post(`/projects/${projectId}/documents`, data),
  // Mat Spec
  getMatSpec:   (projectId: number) => api.get(`/projects/${projectId}/mat-spec`),
  upsertMatSpec:(projectId: number, data: any) => api.post(`/projects/${projectId}/mat-spec`, data),
};
